import { ReactElement } from 'react'

import { List } from 'antd'
import { useTranslation, Trans } from 'react-i18next'
import { Link } from 'react-router-dom'

import { toggleReportsAppDrawer } from 'actions/ui/app'
import { Drawer, Flex, Text } from 'components/core'
import { useAnalytics } from 'features/Monitoring/Segment/useAnalytics'
import { formatCerebroDateTime } from 'helpers/formatting'
import { renderReportActions, renderReportDateRange } from 'helpers/reports'
import { useAction } from 'hooks'
import { getPath } from 'routing/pagePaths'
import { Report } from 'types'

import * as styles from './styles.scss'

interface ReportsDrawerProps {
    newReports: Report[]
    drawerReports: Report[]
    drawerReportsLoading: boolean
    downloadReport: (payload: { reportId: string }) => void
    visible: boolean
    onClose: () => void
}

export const ReportsDrawer = ({
    newReports,
    drawerReports,
    drawerReportsLoading,
    downloadReport,
    visible,
    onClose,
}: ReportsDrawerProps): ReactElement => {
    const { t } = useTranslation(['common', 'navigation'])
    const toggleDrawer = useAction(toggleReportsAppDrawer)
    const closeMe = (): void => {
        toggleDrawer(false)
    }
    const { trackCtaClick } = useAnalytics()

    const onDownload = (payload: { reportId: string }): void => {
        trackCtaClick({
            destination: 'Same Page',
            text: 'Download Export',
            type: 'Button',
            location: 'Exports Drawer',
            extraProps: {
                reportId: payload.reportId,
            },
        })
        downloadReport(payload)
    }

    return (
        <Drawer
            title={
                <Flex direction="row" alignItems="center" gap={8}>
                    <Text variant="headingSm">
                        {t('common:reports', 'Exports')}
                    </Text>
                    <Link
                        type="link"
                        onClick={closeMe}
                        to={getPath('reportsSummaryPage')}
                        style={{ fontSize: 14 }}
                    >
                        {t('common:viewAll')}
                    </Link>
                </Flex>
            }
            onClose={onClose}
            open={visible}
            width={460}
            rootClassName={styles.drawer}
        >
            <List
                dataSource={drawerReports}
                loading={drawerReportsLoading}
                locale={{
                    emptyText: t(
                        'navigation:TopNav.ReportsDrawer.emptyText',
                        'No exports to download.'
                    ),
                }}
                renderItem={(report) => {
                    const reportDateRange = renderReportDateRange(report)

                    return (
                        <List.Item
                            actions={[
                                renderReportActions(
                                    report,
                                    newReports,
                                    onDownload,
                                    t
                                ),
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    <span className="fg-default-tag">
                                        {report.type}
                                    </span>
                                }
                                title={
                                    <strong
                                        style={{
                                            overflowWrap: 'break-word',
                                        }}
                                    >
                                        {report.name}
                                    </strong>
                                }
                                className={styles['drawer-list-item']}
                                description={
                                    <>
                                        {reportDateRange && (
                                            <p>
                                                <strong>
                                                    {t(
                                                        'navigation:TopNav.ReportsDrawer.dateRangeLabel',
                                                        'Date Range:'
                                                    )}
                                                </strong>{' '}
                                                {reportDateRange}
                                            </p>
                                        )}
                                        <p>
                                            <Trans i18nKey="navigation:TopNav.ReportsDrawer.createdDate">
                                                <strong>Created Date:</strong>{' '}
                                                {{
                                                    createdDate:
                                                        formatCerebroDateTime(
                                                            report.created_date
                                                        ),
                                                }}
                                            </Trans>
                                        </p>
                                    </>
                                }
                            />
                        </List.Item>
                    )
                }}
            />
        </Drawer>
    )
}
